import * as React from "react";

import "./checkout-step.scss";

import {setGAUserProperties} from "@Common/libs/ga/xd-react-ga";
import {isPickPack}          from "@Common/logic/item/is-pick-pack";
import {cs}                  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {fragments}           from "@Common/react/fragments";
import {Invoke}              from "@Common/react/invoke";
import {UseState}            from "@Common/react/use-state";
import {HiddenLabels}        from "@Common/ui-components/heavy/checkout/hidden-labels";
import {AlertModal}          from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {formatMoney}         from "@Common/ui-components/language/format-money";
import {cascadeSum}          from "@Common/utils/cascade";
import {isNotEmpty}          from "@Common/utils/objects";
import {parseUrlQuery}       from "@Common/utils/parse-url-query";
import {GuestModal}          from "@Xpresso/routes/common/guest-modal/guest-modal";

import {CustomizeLayout}     from "../common/customize-layout/customize-layout";
import {Acknowledge}         from "./acknowledge/acknowledge";
import {Actions}             from "./actions/actions";
import {CheckoutApproval}    from "./approval/checkout-approval";
import {AutoCorrectCart}     from "./auto-correct-cart/auto-correct-cart";
import {CartProcessingModal} from "./cart-processing-modal/cart-processing-modal";
import {groupSections}       from "./common/group-sections";
import {CreditsSection}      from "./credits-section/credits-section";
import {LoadCart}            from "./load-cart";
import {NonSubsxBenefits}    from "./non-subsx-benefits/non-subsx-benefits";
import {PaymentsSection}     from "./payments-section/payments-section";
import {ProductCardsSection} from "./product-cards/product-cards-section";
import {ShippingSection}     from "./shipping-section/shipping-section";
import {CheckoutStore}       from "./store/checkout-store";
import {Summary}             from "./summary/summary";

export const CheckoutStep = ({match, location}) => cs(
    consumeContext("apis"),
    consumeContext("session"),
    consumeContext("userLevels"),
    consumeContext("routerHistory"),
    ["guestModal", ({}, next) => GuestModal({
        content: "newAccount",
        required: true,
        next
    })],
    ["alertModal", (_, next) => AlertModal({next})],
    ({}, next) => next(),
    ["query", (_, next) => next(parseUrlQuery(location.search))],
    ["cart", (_, next) => LoadCart({
        cartId: match.params.cart_id,
        //forceLoadCart: query?.referrer === "approval-requests",
        next,
    })],
    ({apis, session, query, routerHistory}, next) => query && query['state'] === "succeeded" ? (
        <div className="checkout-step-60w">
            <div className="subsx-sync" xd-qa-type="label" xd-qa-name="Loading">
                Your Profile is being updated. Do not hit back or refresh the page.
            </div>
            {Invoke({
                action: async () => {
                    await apis.next.subscription.sync();
                    routerHistory.replace('/checkout/' + match.params.cart_id);
                    await session.reload();
                },
            })}
        </div>
    ) : (
            next()
    ),
    ({}, next) => match.params.cart_id ? (
        Invoke({
            action: () => setGAUserProperties({idCart: match.params.cart_id}),
            next,
        })
    ) : (
        next()
    ),
    ["autoCorrectCart", (_, next) => AutoCorrectCart({next})],
    ["store", ({cart, autoCorrectCart}, next) => CheckoutStore({
        cart,
        disableQueue: autoCorrectCart.running,
        next,
    })],
    ["cartProcessingModal", ({cart}, next) => CartProcessingModal({cart, next})],
    ({cart, routerHistory}, next) => RedirectOnClosed({cart, next, routerHistory}),
    ({store}, next) => CustomizeLayout({
        active: "checkout",
        addressable: !!store.cart.items.find((item) => item.addressable),
        customizable: store.cart.items.find((item) => item.source !== 'OIA' && !isPickPack(item) && !item.uyoMPB),
        isKit: !!store.cart.items.find((item) => isNotEmpty(item?.components)),
        disabled: ["kit-options", "customization", "distribution-options", "product-options"],
        next,
    }),
    ["summary", ({store}, next) => Summary({store, next})],

    ["acknowledge", (_, next) => Acknowledge({next})],
    ["paymentsSection", ({store, summary}, next) => PaymentsSection({
        store,
        summary,
        next,
    })],
    ["creditsSection", ({store}, next) => CreditsSection({
        store,
        next,
    })],

    ["shippingSection", ({store, autoCorrectCart}, next) => ShippingSection({
        autoCorrecting: autoCorrectCart.running,
        store,
        next,
    })],
    ["productCardsSection", ({store, query}, next) => ProductCardsSection({
        store,
        isFromApproval: query?.referrer === "approval-requests",
        next,
    })],
    ["showErrors", (_, next) => UseState({next})],
    ({userLevels, guestModal}, next) => (
        <div className="checkout-step-60w">
            {userLevels?.isGuest &&
                Invoke({
                    action: () => guestModal.show()
                })
            }
            {next()}
        </div>
    ),

    ["approval", ({cart, query, store}, next) => CheckoutApproval({
        store,
        cart: cart.value,
        disabled: query?.referrer === "approval-requests",
        next,
    })],

    ({store}, next) => fragments(
        HiddenLabels({cart: store.cart}),
        next(),
    ),

    ({
        acknowledge,
        approval,
        store,
        summary,
        query,
        showErrors,
        paymentsSection,
        creditsSection,
        cart,
        autoCorrectCart,
        shippingSection,
        productCardsSection,
        cartProcessingModal,
        alertModal,
    }) => <>
        {Invoke({
            action: () => {
                if (cart.value.status === "processing") {
                    cartProcessingModal.show();
                }
            },
        })}
        {shippingSection.ready && (
            autoCorrectCart.render({
                cart,
                actions: [
                    shippingSection.autoCorrectAction,
                ],
                onError: async (msg) => await alertModal.show({message: "Cannot load cart data: " + msg})
            })
        )}

        <div className="main">
            {approval?.render()}
            {productCardsSection?.render()}
            {shippingSection.render({store})}

            {creditsSection .render({showErrors: showErrors.value})}
            {paymentsSection.render({showErrors})}
        </div>
        <div className="summary">
            <div className="sticky-box">

                <div className="table">
                    <div className="main">
                        {summary.render({
                            creditCards: paymentsSection.selectedCards,
                        })}
                    </div>

                    {cs(
                        ["saved", (_, next) => next(
                            cascadeSum(store.cart.items, "[*].selections.mailingListIds[*]", (ml) => (
                                !ml.hasSubscriptionBenefit ? 0 : (
                                    ml.quantity * (+ml.actualUnitPrice - +ml.unitPrice)
                                )
                            )) +
                            cascadeSum(store.cart.items, "[*]", (item) => (
                                item.selections?.options?.length ? cascadeSum(item.selections.options, "[*]", (op) => (
                                    !op.hasSubscriptionBenefit ? 0 : (
                                        item.selections.quantity * (+op.actualUnitPrice - +op.unitPrice)
                                    )
                                )) : 0
                            )) +
                            cascadeSum(store.cart.items, "[*]", (item) => (
                                !item?.shipping?.hasSubscriptionBenefit ? 0 : (
                                    (+item.shipping.actualShippingCharge - (+item.shipping.shippingCharge - +(item.shipping.weightSurcharge || 0)))
                                )
                            ))
                        )],
                        ({saved}) => saved >= 5 && (
                            <div className="subsx" xd-qa-type="text" xd-qa-name="Subsx Saving Amount">
                                You're saving <b>{formatMoney(saved)}</b> with your XD+ subscription!
                            </div>
                        ),
                    )}
                </div>

                {(!autoCorrectCart.running && query?.referrer !== "approval-requests") && NonSubsxBenefits({
                    cart: store.cart,
                })}

                {acknowledge.render({
                    showErrors: showErrors.value,
                })}

                <div className="loading">
                    {autoCorrectCart.running ? "Validating cart..." : store.saving && "Saving cart..."}
                </div>
                <div className="actions">
                    {Actions({
                        cart,
                        query,
                        loading: store.saving || autoCorrectCart.running,
                        showErrors,
                        sections: groupSections([
                            productCardsSection,
                            shippingSection, creditsSection, paymentsSection, acknowledge, approval
                        ]),
                        cartProcessingModal,
                        approval,
                    })}
                </div>
            </div>
        </div>
    </>
);

const RedirectOnClosed = ({cart, next, routerHistory}) => cs(
    consumeContext("cart"),
    ({cart: rootCart}) => fragments(
        cart.value.status === "closed" && Invoke({
            action: () => {
                if (cart.value.cartId === rootCart.value?.cartId) {
                    rootCart.onChange(null);
                }
                routerHistory.push(`/checkout-success/${cart.value.cartId}`);
            },
        }),
        next(),
    ),
);
